/**
 * BlockEditor - Component for editing individual content blocks
 */
import { useState } from "react";
import { 
  GripVertical, Trash2, ChevronDown, ChevronUp,
  Layout, Type, Image, Video, MousePointer, Code, FileText
} from "lucide-react";
import { FontSelector } from "./FontSelector";

// Block type icons mapping
const BLOCK_ICONS = {
  hero: Layout,
  text: Type,
  image: Image,
  video: Video,
  gallery: Image,
  cta: MousePointer,
  columns: Layout,
  html: Code,
  sponsors: FileText
};

export const BlockEditor = ({ block, onChange, onDelete, onMoveUp, onMoveDown, fonts = [] }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const Icon = BLOCK_ICONS[block.type] || FileText;

  const updateContent = (key, value) => {
    onChange({
      ...block,
      content: { ...block.content, [key]: value }
    });
  };

  const renderBlockFields = () => {
    switch (block.type) {
      case "hero":
        return (
          <div className="space-y-3">
            <input
              type="text"
              value={block.content.title || ""}
              onChange={(e) => updateContent("title", e.target.value)}
              placeholder="Hero Title"
              className="w-full bg-black border border-white/10 text-white px-3 py-2 text-sm"
            />
            <input
              type="text"
              value={block.content.subtitle || ""}
              onChange={(e) => updateContent("subtitle", e.target.value)}
              placeholder="Subtitle"
              className="w-full bg-black border border-white/10 text-white px-3 py-2 text-sm"
            />
            <input
              type="url"
              value={block.content.background_image || ""}
              onChange={(e) => updateContent("background_image", e.target.value)}
              placeholder="Background Image URL"
              className="w-full bg-black border border-white/10 text-white px-3 py-2 text-sm"
            />
            <div className="grid grid-cols-2 gap-3">
              <input
                type="text"
                value={block.content.cta_text || ""}
                onChange={(e) => updateContent("cta_text", e.target.value)}
                placeholder="Button Text"
                className="bg-black border border-white/10 text-white px-3 py-2 text-sm"
              />
              <input
                type="text"
                value={block.content.cta_link || ""}
                onChange={(e) => updateContent("cta_link", e.target.value)}
                placeholder="Button Link"
                className="bg-black border border-white/10 text-white px-3 py-2 text-sm"
              />
            </div>
            {fonts.length > 0 && (
              <div className="flex items-center gap-3">
                <label className="text-xs text-zinc-500 uppercase">Title Font:</label>
                <FontSelector 
                  value={block.content.font} 
                  onChange={(val) => updateContent("font", val)}
                  fonts={fonts}
                />
              </div>
            )}
          </div>
        );
      
      case "text":
        return (
          <div className="space-y-3">
            <input
              type="text"
              value={block.content.heading || ""}
              onChange={(e) => updateContent("heading", e.target.value)}
              placeholder="Section Heading (optional)"
              className="w-full bg-black border border-white/10 text-white px-3 py-2 text-sm"
            />
            <textarea
              value={block.content.body || ""}
              onChange={(e) => updateContent("body", e.target.value)}
              placeholder="Content (HTML supported)"
              rows={6}
              className="w-full bg-black border border-white/10 text-white px-3 py-2 text-sm resize-none"
            />
            {fonts.length > 0 && (
              <div className="flex items-center gap-3">
                <label className="text-xs text-zinc-500 uppercase">Font:</label>
                <FontSelector 
                  value={block.content.font} 
                  onChange={(val) => updateContent("font", val)}
                  fonts={fonts}
                />
              </div>
            )}
          </div>
        );
      
      case "image":
        return (
          <div className="space-y-3">
            <input
              type="url"
              value={block.content.src || ""}
              onChange={(e) => updateContent("src", e.target.value)}
              placeholder="Image URL"
              className="w-full bg-black border border-white/10 text-white px-3 py-2 text-sm"
            />
            <input
              type="text"
              value={block.content.alt || ""}
              onChange={(e) => updateContent("alt", e.target.value)}
              placeholder="Alt Text"
              className="w-full bg-black border border-white/10 text-white px-3 py-2 text-sm"
            />
            <input
              type="text"
              value={block.content.caption || ""}
              onChange={(e) => updateContent("caption", e.target.value)}
              placeholder="Caption (optional)"
              className="w-full bg-black border border-white/10 text-white px-3 py-2 text-sm"
            />
            {block.content.src && (
              <img src={block.content.src} alt="Preview" className="max-h-32 object-contain" />
            )}
          </div>
        );
      
      case "video":
        return (
          <div className="space-y-3">
            <input
              type="url"
              value={block.content.url || ""}
              onChange={(e) => updateContent("url", e.target.value)}
              placeholder="YouTube or Vimeo URL"
              className="w-full bg-black border border-white/10 text-white px-3 py-2 text-sm"
            />
            <input
              type="text"
              value={block.content.title || ""}
              onChange={(e) => updateContent("title", e.target.value)}
              placeholder="Video Title"
              className="w-full bg-black border border-white/10 text-white px-3 py-2 text-sm"
            />
          </div>
        );
      
      case "cta":
        return (
          <div className="space-y-3">
            <input
              type="text"
              value={block.content.heading || ""}
              onChange={(e) => updateContent("heading", e.target.value)}
              placeholder="CTA Heading"
              className="w-full bg-black border border-white/10 text-white px-3 py-2 text-sm"
            />
            <textarea
              value={block.content.text || ""}
              onChange={(e) => updateContent("text", e.target.value)}
              placeholder="Supporting text"
              rows={2}
              className="w-full bg-black border border-white/10 text-white px-3 py-2 text-sm resize-none"
            />
            <div className="grid grid-cols-2 gap-3">
              <input
                type="text"
                value={block.content.buttons?.[0]?.text || ""}
                onChange={(e) => updateContent("buttons", [
                  { ...block.content.buttons?.[0], text: e.target.value },
                  block.content.buttons?.[1]
                ].filter(Boolean))}
                placeholder="Primary Button Text"
                className="bg-black border border-white/10 text-white px-3 py-2 text-sm"
              />
              <input
                type="text"
                value={block.content.buttons?.[0]?.link || ""}
                onChange={(e) => updateContent("buttons", [
                  { ...block.content.buttons?.[0], link: e.target.value },
                  block.content.buttons?.[1]
                ].filter(Boolean))}
                placeholder="Primary Button Link"
                className="bg-black border border-white/10 text-white px-3 py-2 text-sm"
              />
            </div>
          </div>
        );
      
      case "html":
        return (
          <textarea
            value={block.content.html || ""}
            onChange={(e) => updateContent("html", e.target.value)}
            placeholder="<div>Custom HTML...</div>"
            rows={8}
            className="w-full bg-black border border-white/10 text-white px-3 py-2 text-sm font-mono resize-none"
          />
        );
      
      default:
        return (
          <div className="text-zinc-500 text-sm">
            Block type &quot;{block.type}&quot; editor not implemented yet
          </div>
        );
    }
  };

  return (
    <div className="bg-zinc-900/50 border border-white/10">
      <div 
        className="flex items-center gap-3 p-3 cursor-pointer hover:bg-white/5"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <GripVertical className="w-4 h-4 text-zinc-600" />
        <Icon className="w-4 h-4 text-zinc-400" />
        <span className="text-white text-sm font-medium flex-1 capitalize">{block.type}</span>
        <button
          onClick={(e) => { e.stopPropagation(); onMoveUp(); }}
          className="p-1 text-zinc-500 hover:text-white"
        >
          <ChevronUp className="w-4 h-4" />
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); onMoveDown(); }}
          className="p-1 text-zinc-500 hover:text-white"
        >
          <ChevronDown className="w-4 h-4" />
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); onDelete(); }}
          className="p-1 text-zinc-500 hover:text-red-500"
        >
          <Trash2 className="w-4 h-4" />
        </button>
        {isExpanded ? <ChevronUp className="w-4 h-4 text-zinc-400" /> : <ChevronDown className="w-4 h-4 text-zinc-400" />}
      </div>
      
      {isExpanded && (
        <div className="p-4 border-t border-white/10">
          {renderBlockFields()}
        </div>
      )}
    </div>
  );
};

export default BlockEditor;
